import React, { useState, useRef, useEffect } from "react";

import css from "./form.module.scss";
import { IconButton, TextField } from "@mui/material";
import { ValidatorFn } from "misc/validators";

export interface Props {
  type?: string;
  label?: string;
  autoHideLabel?: boolean;
  initialValue: string;
  updateValue: (value: string) => void;
  validator?: ValidatorFn;
  autocomplete?: string;
  endIcon?: any;
  iconAction?: () => void;
  isRequired?: boolean;
  labelOutside?: boolean;
  placeholder?: string;
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  readOnly?: boolean;
}

const TextInput: React.FC<Props> = (props) => {
  const {
    label,
    initialValue,
    updateValue,
    validator,
    autocomplete,
    type = "text",
    endIcon,
    iconAction,
    isRequired = false,
    labelOutside = false,
    placeholder = "",
    onKeyPress,
    readOnly = false,
  } = props;

  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string | null | undefined>(undefined);
  const [, setFocused] = useState<boolean>(false);

  const [updateTimeoutHandle, setUpdateTimeoutHandle] = useState<any>(null);

  const updateTimeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  updateTimeoutHandleRef.current = updateTimeoutHandle;

  /////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (value !== initialValue) setValue(initialValue);
    // eslint-disable-next-line
  }, [initialValue]);
  /////////////////////////////////////////////////////////////////////////

  const onChange = (value: string) => {
    if (updateTimeoutHandleRef.current)
      clearTimeout(updateTimeoutHandleRef.current);

    setValue(value);

    const timeout = setTimeout(() => updateValue(value), 500);
    setUpdateTimeoutHandle(timeout);
  };

  const onBlur = () => {
    setFocused(false);
    const validationError = validator && validator(value);
    setError(validationError);
  };

  /////////////////////////////////////////////////////////////////////////

  return (
    <div>
      {label && labelOutside && (
        <label className={css.externalLabel}>
          {label}
          {isRequired && <span className={css.required}>*</span>}
        </label>
      )}
      <div className={css.inputContainer}>
        {label && !labelOutside && (
          <label className={css.inputLabel}>
            {label}
            {isRequired && <span className={css.required}>*</span>}
          </label>
        )}
        <TextField
          className={css.textinput}
          fullWidth
          variant="standard"
          margin="none"
          type={type}
          placeholder={placeholder}
          value={value}
          error={!!error}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          autoComplete={autocomplete}
          InputProps={{
            disableUnderline: true,
            readOnly: readOnly,
            ...(endIcon && {
              endAdornment: (
                <IconButton
                  onClick={iconAction}
                  tabIndex={-1}
                  size="small"
                  className={css.iconButton}
                >
                  {endIcon}
                </IconButton>
              ),
            }),
          }}
        />
      </div>
      {error && <div className={css.errorText}>{error}</div>}
    </div>
  );
};

export default TextInput;
